import _extends from '@babel/runtime/helpers/extends';
import React__default, { useState, useRef, useLayoutEffect, useCallback, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { s as styleTextAreaMixin } from '../TextAreaFormGroup/TextAreaFormGroup-AEqlDU5R.js';
import { A as AttachmentPill } from './AttachmentPill-DuloRzCp.js';
import { V as ValidationLine } from '../FormGroup/ValidationLine-DjKYKrQz.js';
import { F as FileAttachmentButton } from './FileAttachmentButton-CigQBnEu.js';
import { S as SendButton } from './SendButton-C-xnREkx.js';
import { g as getTranslationComponent } from '../i18n/ChatBoxInputTranslation/index-BmD8Abdb.js';
import { B as BASE_INPUT_HEIGHT, M as MAX_ATTACHMENT_SIZE, a as MAX_ROWS, I as INPUT_PADDING } from './constants-BPNgncOf.js';
import { f as focusOutline } from '../theme/style-mixins/FocusIndicator/focusOutline-oDdaDZ4N.js';
import { v as verifyAttachmentType } from './utils/verifyAttachmentType-DyWhJua4.js';
import { S as StopButton } from './StopButton-CVzAIaWs.js';
import { g as getAttachmentLimit } from './utils/getAttachmentLimit-C3TBgjYw.js';
import { f as formatBytes } from './utils/formatBytes-DScrFC7W.js';
import { F as Flex } from '../Flex/Flex-77mGg7gI.js';
import { I as IntlProvider } from '../LocalModules/storm-ui-intl/dist/esm/IntlProvider-DUNZ897o-Dn-1_05J.js';
import { u as useIntl } from '../LocalModules/storm-ui-intl/dist/esm/3rdPartyModules/react-intl/lib/src/components/useIntl-voJ4yxcS-DU7lWLPF.js';
import { K as KEYBOARD_KEYNAMES } from '../LocalModules/storm-ui-utils/dist/esm/keyboardKeynames-CVa_vjg3-CVa_vjg3.js';
import { v as v4 } from '../3rdPartyModules/uuid/dist/esm-browser/v4-CYEyAeRz.js';
import { a as TaktIdConsumer } from '../TaktIdContext/TaktIdContext-C6vdL-6Z.js';
import { c as createStormTaktId } from '../TaktIdContext/TaktIdContext.utils-B0CzqlN8.js';
import { I as InlineAlert } from '../InlineAlert/InlineAlert-DGAsFX2G.js';

const Container = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-40026394__sc-nwig80-0"
})(["border:", ";border-style:", ";box-shadow:", ";padding-inline:", ";padding-block:", ";border-radius:", ";background-color:", ";color:", ";display:flex;flex-direction:column;gap:", ";", " ", " :focus-within{", "}border-style:", ";"], ({
  theme
}) => theme.border.medium.default, ({
  $drag
}) => $drag ? 'dashed' : 'solid', ({
  theme
}) => theme.input.innerShadow, ({
  theme
}) => theme.controls.paddingInline.large.default, ({
  theme
}) => theme.controls.paddingBlock.base.default, ({
  theme
}) => theme.border.radius.medium, ({
  theme
}) => theme.input.backgroundColor.default, ({
  theme
}) => theme.text.color.default, ({
  theme
}) => theme.spacing.xxxsmall.default, ({
  $drag
}) => $drag && css(["background-color:", ";"], ({
  theme
}) => theme.chatBoxInput.backgroundColor.drag), ({
  disabled,
  theme
}) => disabled && css(["color:", ";background-color:", ";border:", ";"], theme.text.color.unavailable, theme.input.backgroundColor.unavailable, theme.border.medium.unavailable), focusOutline, ({
  $drag
}) => $drag ? 'dotted' : 'solid');
const InputGroup = /*#__PURE__*/styled(({
  children,
  inputGroupRef,
  className
}) => /*#__PURE__*/React__default.createElement("div", {
  className: className,
  ref: inputGroupRef
}, children)).withConfig({
  componentId: "sc-storm-ui-40026394__sc-nwig80-1"
})(["border:none;box-sizing:border-box;min-width:auto;display:flex;pointer-events:", ";> textarea{", " margin-inline-end:-", ";padding-inline:0;pointer-events:", ";}"], ({
  $drag
}) => $drag ? 'none' : 'auto', styleTextAreaMixin, ({
  theme
}) => theme.controls.paddingInline.large.default, ({
  $drag
}) => $drag ? 'none' : 'auto');
const AttachmentRow = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-40026394__sc-nwig80-2"
})(["display:flex;justify-content:flex-start;align-items:flex-end;gap:", ";flex-wrap:wrap;"], ({
  theme
}) => theme.spacing.xxxsmall.default);
const ActionBarWrapper = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-40026394__sc-nwig80-3"
})(["display:flex;justify-content:space-between;align-items:center;"]);
const ValidationLoadingWrapper = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-40026394__sc-nwig80-4"
})(["height:", ";padding-inline:", ";padding-block:", ";display:flex;align-items:center;"], ({
  theme
}) => theme.chatBoxInput.attachment.height, ({
  theme
}) => theme.chatBoxInput.attachment.padding.inline, ({
  theme
}) => theme.chatBoxInput.attachment.padding.block);
const ActionBar = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-40026394__sc-nwig80-5"
})(["width:100%;"]);
const StyledTextArea = /*#__PURE__*/styled('textarea').withConfig({
  componentId: "sc-storm-ui-40026394__sc-nwig80-6"
})(["", " padding:unset;resize:none;max-height:", "px;overflow:auto;box-sizing:border-box;"], styleTextAreaMixin, BASE_INPUT_HEIGHT * MAX_ROWS - MAX_ROWS - INPUT_PADDING);
const MessageContainer = /*#__PURE__*/styled(Flex).withConfig({
  componentId: "sc-storm-ui-40026394__sc-nwig80-7"
})(["margin-block-start:", ";"], ({
  theme
}) => theme.spacing.xxxsmall.default);
const ChatBoxValidationMessage = /*#__PURE__*/styled(InlineAlert).withConfig({
  componentId: "sc-storm-ui-40026394__sc-nwig80-8"
})(["margin-top:0;margin-bottom:0;"]);
const ChatBox = ({
  id,
  value = '',
  attachments: inputAttachments,
  disabled,
  disableSend,
  placeholder,
  inputRef,
  locale,
  mode = 'send',
  onChange,
  onAttach,
  onSend,
  onStop,
  renderActionBar,
  taktId,
  taktValue,
  onAttachmentClose,
  errorMessage,
  validateAttachment,
  acceptedAttachmentTypes,
  maxAttachments,
  maxAttachmentSize = MAX_ATTACHMENT_SIZE,
  ...rest
}) => {
  const [isActiveDrag, setActiveDrag] = useState(false);
  const [errors, setErrors] = useState([]);
  const [inputValue, setInputValue] = useState(value);
  const attachmentLimit = getAttachmentLimit(maxAttachments);
  const getInitialAttachments = () => {
    const initialAttachments = [];
    if (inputAttachments) {
      for (const att of inputAttachments) {
        const uniqueId = v4();
        initialAttachments.push({
          file: att,
          id: uniqueId
        });
      }
    }
    return initialAttachments;
  };
  const [attachments, setAttachments] = useState(getInitialAttachments());
  const [isValidating, setIsValidating] = useState(false);
  const textAreaRef = useRef(null);
  const {
    formatMessage
  } = useIntl();
  useLayoutEffect(() => {
    /** Adjust size at render */
    resize();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resize = useCallback((clearInput = false) => {
    if (textAreaRef.current) {
      if (clearInput) {
        textAreaRef.current.innerText = '';
        textAreaRef.current.style.height = `${BASE_INPUT_HEIGHT}px`;
        return;
      }
      if (!textAreaRef.current.value) {
        textAreaRef.current.style.height = `${BASE_INPUT_HEIGHT}px`;
        return;
      }
      // removing the height allows us to validate the scroll height
      textAreaRef.current.style.removeProperty('height');
      if (textAreaRef.current.scrollHeight > BASE_INPUT_HEIGHT) {
        const updatedHeight = textAreaRef.current.scrollHeight;
        textAreaRef.current.style.height = `${updatedHeight}px`;
      } else {
        textAreaRef.current.style.height = `${BASE_INPUT_HEIGHT}px`;
      }
    }
  }, []);
  const handleChange = useCallback(event => {
    onChange === null || onChange === void 0 || onChange(event);
    setInputValue(event.target.value);
    resize();
  }, [onChange, resize]);
  const handleSend = useCallback(() => {
    const files = attachments.map(attachment => attachment.file);
    onSend === null || onSend === void 0 || onSend(inputValue, files);
    setInputValue('');
    setAttachments([]);
    setErrors([]);
    resize(true);
  }, [attachments, inputValue, onSend, resize]);
  const handleKeydown = useCallback(event => {
    const {
      key,
      shiftKey,
      altKey
    } = event;
    if (key === KEYBOARD_KEYNAMES.ENTER) {
      if (mode === 'stop' || disableSend) {
        event.preventDefault();
        return;
      }
      if (shiftKey || altKey) {
        var _textAreaRef$current$, _textAreaRef$current, _textAreaRef$current$2, _textAreaRef$current2;
        const startPos = (_textAreaRef$current$ = (_textAreaRef$current = textAreaRef.current) === null || _textAreaRef$current === void 0 ? void 0 : _textAreaRef$current.selectionStart) !== null && _textAreaRef$current$ !== void 0 ? _textAreaRef$current$ : 0;
        const endPos = (_textAreaRef$current$2 = (_textAreaRef$current2 = textAreaRef.current) === null || _textAreaRef$current2 === void 0 ? void 0 : _textAreaRef$current2.selectionEnd) !== null && _textAreaRef$current$2 !== void 0 ? _textAreaRef$current$2 : 0;
        setInputValue(`${inputValue.substring(0, startPos)}${altKey ? '\n' : ''}${inputValue.substring(endPos, inputValue.length)}`);
        window.setTimeout(() => {
          var _textAreaRef$current3;
          (_textAreaRef$current3 = textAreaRef.current) === null || _textAreaRef$current3 === void 0 || _textAreaRef$current3.setSelectionRange(startPos + 1, endPos + 1);
        }, 0);
      } else {
        event.preventDefault();
        handleSend();
      }
    }
  }, [handleSend, inputValue, mode]);
  const handleAttachmentClose = useCallback((file, index) => {
    const filteredAttachments = attachments.filter((_att, attachmentIndex) => attachmentIndex !== index);
    setAttachments(filteredAttachments);
    if (filteredAttachments.length <= attachmentLimit) {
      setErrors([]);
    }
    onAttachmentClose === null || onAttachmentClose === void 0 || onAttachmentClose(file, index);
  }, [attachmentLimit, attachments, onAttachmentClose]);
  const handleFileUpload = useCallback(async (event, fileList) => {
    const validFiles = [];
    const fileErrors = [];
    let attachmentCount = attachments.length;
    for await (const file of fileList) {
      if (file) {
        // Cannot attach more than 6 files
        if (attachmentCount >= attachmentLimit) {
          fileErrors.push(formatMessage({
            id: 'attachment-count-error'
          }, {
            n: attachmentLimit
          }));
          break;
        }

        // File cannot be larger than 5MB
        if (file.size > maxAttachmentSize) {
          fileErrors.push(formatMessage({
            id: 'attachment-size-error'
          }, {
            attachment: file.name,
            size: formatBytes(maxAttachmentSize)
          }));
          continue; // eslint-disable-line no-continue
        }
        const uuid = v4();
        if (validateAttachment) {
          setIsValidating(true);
          let validation;
          try {
            validation = await Promise.resolve(validateAttachment(file, fileList.indexOf(file)));
          } catch (e) {
            fileErrors.push(formatMessage({
              id: 'attachment-fetch-error'
            }, {
              fileName: file.name
            }));
          }
          if (validation && 'isValid' in validation) {
            if (validation.isValid === true) {
              validFiles.push({
                file,
                id: uuid
              });
              attachmentCount += 1;
            } else if (validation && 'message' in validation && validation.message) {
              fileErrors.push(validation.message);
            }
          }
        } else {
          validFiles.push({
            file,
            id: uuid
          });
          attachmentCount += 1;
        }
      }
    }
    setErrors(fileErrors);
    setIsValidating(false);
    if (validFiles.length) {
      setAttachments([...attachments, ...validFiles]);
      onAttach === null || onAttach === void 0 || onAttach(event, validFiles.map(validFile => validFile.file));
    }
  }, [attachmentLimit, attachments, formatMessage, maxAttachmentSize, onAttach, validateAttachment]);
  useEffect(() => {
    if (attachments.length) {
      var _document$getElementB;
      const attachmentIndex = attachments.length - 1;
      const attachmentToFocus = `${attachments[attachmentIndex].file.name}-${attachmentIndex}`;
      (_document$getElementB = document.getElementById(attachmentToFocus)) === null || _document$getElementB === void 0 || _document$getElementB.focus();
    }
  }, [attachments]);
  const handleDragOver = event => {
    setActiveDrag(true);
    event.preventDefault();
  };
  const handleDragLeave = event => {
    setActiveDrag(false);
    event.preventDefault();
  };
  const handleDrop = useCallback(event => {
    event.preventDefault();
    setActiveDrag(false);
    const fileList = event.type === 'drop' ? event.dataTransfer.files : event.target.files;
    const fileNames = [...fileList].filter(file => verifyAttachmentType(file, acceptedAttachmentTypes));
    handleFileUpload(event, fileNames);
  }, [acceptedAttachmentTypes, handleFileUpload]);
  return /*#__PURE__*/React__default.createElement(TaktIdConsumer, {
    taktId: taktId,
    taktValue: taktValue,
    fallbackId: createStormTaktId('chat-box-input')
  }, ({
    getDataTaktAttributes
  }) => /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Container, {
    id: id,
    disabled: disabled,
    onDrop: handleDrop,
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    $drag: isActiveDrag
  }, /*#__PURE__*/React__default.createElement(InputGroup, {
    inputGroupRef: inputRef
  }, /*#__PURE__*/React__default.createElement(StyledTextArea, _extends({}, rest, getDataTaktAttributes({
    taktIdSuffix: 'text-area'
  }), {
    id: `${id}-textarea`,
    ref: textAreaRef,
    value: inputValue,
    placeholder: placeholder,
    onChange: handleChange,
    onKeyDown: handleKeydown,
    disabled: disabled,
    rows: 1
  }))), /*#__PURE__*/React__default.createElement(AttachmentRow, null, attachments.length ? attachments.map((attachment, index) => /*#__PURE__*/React__default.createElement(AttachmentPill, {
    key: `${attachment.file.name}-${attachment.id}`,
    attachment: attachment.file,
    onClose: () => handleAttachmentClose(attachment.file, index),
    disabled: disabled,
    locale: locale
  })) : null, isValidating && /*#__PURE__*/React__default.createElement(ValidationLoadingWrapper, null, /*#__PURE__*/React__default.createElement(InlineAlert, {
    variant: "loading",
    message: formatMessage({
      id: 'attachment-validate-message'
    })
  }))), /*#__PURE__*/React__default.createElement(ActionBarWrapper, null, /*#__PURE__*/React__default.createElement(ActionBar, null, renderActionBar ? renderActionBar({
    id: `${id}-file-upload-button`,
    onChange: handleFileUpload,
    disabled: isValidating || disabled,
    acceptedAttachmentTypes
  }) : /*#__PURE__*/React__default.createElement(FileAttachmentButton, {
    id: `${id}-file-upload-button`,
    onChange: handleFileUpload,
    disabled: isValidating || disabled,
    acceptedAttachmentTypes: acceptedAttachmentTypes,
    multiple: attachmentLimit > 1
  })), mode === 'stop' ? /*#__PURE__*/React__default.createElement(StopButton, {
    onClick: onStop,
    disabled: isValidating || disabled
  }) : /*#__PURE__*/React__default.createElement(SendButton, {
    onClick: handleSend,
    disabled: isValidating || disabled || disableSend,
    isActive: !!attachments.length || !!inputValue.length
  }))), (errors.length || errorMessage) && /*#__PURE__*/React__default.createElement(MessageContainer, {
    direction: "column",
    gap: "xxxsmall"
  }, errors.map(error => /*#__PURE__*/React__default.createElement(ValidationLine, {
    key: error,
    role: "alert",
    "aria-live": "assertive",
    id: `${id}-chatbox-error`
  }, /*#__PURE__*/React__default.createElement(ChatBoxValidationMessage, {
    variant: "error",
    message: error
  }))), errorMessage && /*#__PURE__*/React__default.createElement(ValidationLine, {
    role: "alert",
    "aria-live": "assertive",
    id: `${id}-chatbox-user-error`
  }, /*#__PURE__*/React__default.createElement(ChatBoxValidationMessage, {
    variant: "error",
    message: errorMessage
  })))));
};
const ChatBoxInput = ({
  locale,
  ...rest
}) => {
  const translations = getTranslationComponent(locale);
  return /*#__PURE__*/React__default.createElement(IntlProvider, {
    translations: translations
  }, /*#__PURE__*/React__default.createElement(ChatBox, rest));
};

export { ChatBoxInput as C };
//# sourceMappingURL=ChatBoxInput-BprMQz_W.js.map
